import { useEffect, useState } from "react";
import { Users, ShieldCheck, Activity, UserX } from "lucide-react";
import api from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/shared/States";

function Kpi({ testId, icon: Icon, label, value, sub }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-jigsaw-border bg-white p-4">
      <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[.08em] text-jigsaw-muted"><Icon className="h-3.5 w-3.5 text-jigsaw-orange" /> {label}</div>
      <p className="mt-2 text-[26px] font-semibold leading-none text-jigsaw-navy">{value}</p>
      {sub && <p className="mt-1 text-[12px] text-jigsaw-muted">{sub}</p>}
    </div>
  );
}

export function AdminOverview({ onGoToUsers }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)).catch(() => setError(true)); }, []);
  if (error) return <ErrorState testId="admin-overview-error" />;
  if (!data) return <LoadingState testId="admin-overview-loading" />;
  return (
    <div data-testid="admin-overview" className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="admin-kpi-users" icon={Users} label="Active accounts" value={data.users_active} sub={`${data.users_inactive} inactive · ${data.users_without_membership} without membership`} />
        <Kpi testId="admin-kpi-admins" icon={ShieldCheck} label="Jigsaw Admins" value={data.admins_active} sub="Last active Admin can never be removed" />
        <Kpi testId="admin-kpi-sessions" icon={Activity} label="Live sessions" value={data.live_sessions} sub={`${data.session_controls.inactivity_timeout_minutes}-minute inactivity sign-out`} />
        <Kpi testId="admin-kpi-pilot" icon={UserX} label="Pilot-eligible" value={data.pilot_eligible} sub={data.session_controls.otp_dev_echo ? "OTP shown on screen (development)" : "OTP delivered by e-mail"} />
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <section className="rounded-lg border border-jigsaw-border bg-white p-4">
          <h2 className="text-base font-semibold text-jigsaw-navy">Memberships by role</h2>
          <ul data-testid="admin-roles-breakdown" className="mt-3 space-y-1.5 text-[13px]">
            {data.memberships_by_role.map((r) => (
              <li key={r.role} className="flex items-center justify-between border-b border-dashed border-jigsaw-border pb-1.5 last:border-0">
                <button type="button" data-testid={`admin-role-filter-${r.role}`} className="text-left text-jigsaw-navy hover:text-jigsaw-orange" onClick={() => onGoToUsers?.({ role: r.role })}>{r.role.replace("_", " ")}</button>
                <span className="font-mono-ref text-jigsaw-muted">{r.count}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="rounded-lg border border-jigsaw-border bg-white p-4">
          <h2 className="text-base font-semibold text-jigsaw-navy">Recent account actions</h2>
          {data.recent_admin_actions.length === 0 ? <p data-testid="admin-recent-empty" className="mt-3 text-[13px] text-jigsaw-muted">No account changes recorded yet.</p> : (
            <ul data-testid="admin-recent-actions" className="mt-3 space-y-2 text-[13px]">
              {data.recent_admin_actions.map((a) => (
                <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <span><span className="font-mono-ref text-jigsaw-navy">{a.action}</span> <span className="text-jigsaw-muted">by {a.actor_name}</span></span>
                  <span className="text-[12px] text-jigsaw-muted">{new Date(a.created_at).toLocaleString("en-GB")}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
